/**
 * VÉRAULT Travels — Post-login Suite Foyer (dark editorial · Feb 2026)
 *
 * Renders at `/suite/:slug/welcome` AFTER successful authentication.
 * Uses the approved dark VÉRAULT Member Suite design language
 * (matching the access/login page) — black background, gold small
 * caps, large serif, italic supporting copy, gold "ENTER YOUR SUITE"
 * button, quiet private footer.
 *
 * NEVER reveals personalized content before login. The /suite/:slug
 * route redirects to /suite/:slug/access first; this welcome page
 * additionally verifies the authenticated user actually owns this
 * trip before rendering. Unauthorized visitors are bounced back to
 * the access door.
 */
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, LogOut } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function verifyClientOwnsTrip(tripId) {
  try {
    const { data } = await axios.get(
      `${BACKEND_URL}/api/client-portal/trips/${tripId}`,
      { withCredentials: true }
    );
    return Boolean(data && (data.id === tripId || data.trip_id === tripId));
  } catch {
    return false;
  }
}

export default function SuiteLandingPublic() {
  const { slug } = useParams();
  const slugLower = (slug || "").toLowerCase();
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);

  // Feb 2026 — remember the last suite slug this client used so the
  // portal exit button can bring them back to *their* foyer (not a
  // hardcoded one) on the way to a full sign-out.
  React.useEffect(() => {
    if (slugLower) {
      try { window.localStorage.setItem("verault_suite_slug", slugLower); } catch { /* noop */ }
    }
  }, [slugLower]);

  // Two-step sign-out from the foyer:
  //   foyer  → click "exit"  → full logout → /suite/{slug}/access
  const handleFoyerSignOut = async () => {
    try { await logout?.(); } catch { /* noop */ }
    navigate(`/suite/${slugLower}/access`, { replace: true });
  };

  // Auth + ownership gate
  React.useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      // Not authenticated → bounce back to the access door
      if (!user || (user.role || "").toLowerCase() !== "client") {
        navigate(`/suite/${slugLower}/access`, { replace: true });
        return;
      }
      // Resolve slug → trip_id and verify ownership
      try {
        const ref = await axios.get(
          `${BACKEND_URL}/api/suite-landing/${slugLower}/trip-ref`,
          { withCredentials: true }
        );
        const tid = ref?.data?.trip_id;
        if (!tid) {
          if (!cancelled) setErr("not_found");
          return;
        }
        const owns = await verifyClientOwnsTrip(tid);
        if (!owns) {
          // Authenticated but not owner of this slug — bounce to access
          // page so they can try a different account.
          navigate(`/suite/${slugLower}/access`, { replace: true });
          return;
        }
        // Owner confirmed — fetch the foyer payload
        const { data: payload } = await axios.get(
          `${BACKEND_URL}/api/suite-landing/${slugLower}`,
          { withCredentials: true }
        );
        if (!cancelled) {
          setData(payload);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.status === 404 ? "not_found" : "unavailable");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, slugLower, navigate]);

  const enterSuite = () => navigate("/portal/client", { replace: false });

  if (authLoading || loading) {
    return (
      <main className="min-h-screen bg-black flex items-center justify-center" data-testid="suite-foyer-loading">
        <Loader2 className="size-5 animate-spin text-[#c9a961]" strokeWidth={1.5} />
      </main>
    );
  }

  if (err === "not_found") {
    return (
      <main className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="suite-foyer-not-found">
        <div className="max-w-md text-center space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#c9a961]">
            VÉRAULT · MEMBER SUITE
          </p>
          <h1 className="font-serif text-[28px] sm:text-[34px] leading-tight">
            We couldn&apos;t find this suite
          </h1>
          <p className="font-accent italic text-[14px] text-white/60 leading-relaxed">
            Please check your link or reply to your advisor&apos;s note for a fresh entry.
          </p>
        </div>
      </main>
    );
  }

  if (err) {
    return (
      <main className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <p className="font-accent italic text-[14px] text-white/60">
          The suite is briefly unavailable. Please try again in a moment.
        </p>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-black text-white"
      data-testid="suite-foyer"
      data-door-slug={slugLower}
    >
      <div className="max-w-2xl lg:max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 py-10 sm:py-14 lg:py-20">
        {/* Top brand bar — eyebrow on the left, "sign out" on the right
            (returns to the access door once the user is fully logged out). */}
        <div className="flex items-start justify-between gap-3">
          <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-[#c9a961]">
            {data.eyebrow || "VÉRAULT · MEMBER SUITE"}
          </p>
          <button
            type="button"
            onClick={handleFoyerSignOut}
            className="inline-flex items-center justify-center size-9 rounded-md text-[#7d7569] hover:text-white hover:bg-white/5 transition-colors"
            data-testid="suite-foyer-sign-out"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut className="size-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Feb 2026 — Hero imagery layouts.
            · editorial_split (desktop): 1 large anchor + 1 smaller supporting.
              Mobile collapses to a vertical stack (Editorial Stack).
            · editorial_stack: vertical stack on all sizes.
            · single_hero: single anchor image, no companion.
            All variants pull `hero_image_primary` (+ optional secondary).
            Pexels page URLs auto-resolve to direct CDN URLs server-side. */}
        {(data.hero_image_primary || data.hero_image_secondary) && (() => {
          const layout = (data.image_layout || "editorial_split").toLowerCase();
          const hasSecondary = Boolean(data.hero_image_secondary);
          if (layout === "single_hero" || !hasSecondary) {
            return (
              <div className="mt-10 sm:mt-14" data-testid="suite-foyer-hero" data-layout="single">
                <div className="relative overflow-hidden rounded-sm aspect-[3/4] sm:aspect-[16/10]">
                  <img src={data.hero_image_primary} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90"
                       data-testid="suite-foyer-hero-primary" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
                </div>
              </div>
            );
          }
          if (layout === "soft_carousel") {
            return (
              <SoftCarouselHero
                images={[data.hero_image_primary, data.hero_image_secondary].filter(Boolean)}
              />
            );
          }
          if (layout === "editorial_stack") {
            return (
              <div className="mt-10 sm:mt-14 grid grid-cols-1 gap-3" data-testid="suite-foyer-hero" data-layout="stack">
                {data.hero_image_primary && (
                  <div className="relative overflow-hidden rounded-sm aspect-[4/3]">
                    <img src={data.hero_image_primary} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90"
                         data-testid="suite-foyer-hero-primary" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
                  </div>
                )}
                {data.hero_image_secondary && (
                  <div className="relative overflow-hidden rounded-sm aspect-[4/3]">
                    <img src={data.hero_image_secondary} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90"
                         data-testid="suite-foyer-hero-secondary" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
                  </div>
                )}
              </div>
            );
          }
          // editorial_split — desktop asymmetric (anchor 2fr + supporter 1fr),
          // mobile collapses to a vertical stack (per spec).
          return (
            <div
              className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3 sm:gap-4"
              data-testid="suite-foyer-hero"
              data-layout="split"
            >
              {data.hero_image_primary && (
                <div className="relative overflow-hidden rounded-sm aspect-[4/3] sm:aspect-[5/6]">
                  <img src={data.hero_image_primary} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90"
                       data-testid="suite-foyer-hero-primary" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
                </div>
              )}
              {data.hero_image_secondary && (
                <div className="relative overflow-hidden rounded-sm aspect-[4/3] sm:aspect-[3/4] sm:mt-10">
                  <img src={data.hero_image_secondary} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90"
                       data-testid="suite-foyer-hero-secondary" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
                </div>
              )}
            </div>
          );
        })()}

        {/* Editorial body */}
        <section className="mt-16 sm:mt-24 space-y-6">
          {/* Display title — responsive scale, controlled mobile wrap. */}
          <h1
            className="font-serif text-white leading-[1.05] tracking-tight text-[clamp(32px,7.5vw,56px)] max-w-[14ch] sm:max-w-none whitespace-pre-line"
            data-testid="suite-foyer-title"
          >
            {data.title}
          </h1>
          {data.show_prepared_for && data.travelers && (
            <p
              className="font-serif italic text-[16px] sm:text-[18px] text-white/85"
              data-testid="suite-foyer-travelers"
            >
              <span className="block">Prepared for</span>
              <span className="block" data-testid="suite-foyer-travelers-names">{data.travelers}</span>
            </p>
          )}
          {data.journey && (
            <div className="space-y-1" data-testid="suite-foyer-journey-block">
              <p className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-white/55">
                The Journey
              </p>
              <p
                className="font-accent italic text-[15px] sm:text-[16px] text-white/80"
                data-testid="suite-foyer-journey"
              >
                {data.journey}
              </p>
            </div>
          )}
          {(data.dates_of_experience || data.dates) && (
            <div className="space-y-1" data-testid="suite-foyer-dates-block">
              <p className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-white/55">
                Dates of Experience
              </p>
              <p
                className="font-mono text-[11.5px] uppercase tracking-[0.22em] text-white/85"
                data-testid="suite-foyer-dates"
              >
                {data.dates_of_experience || data.dates}
              </p>
            </div>
          )}
          {data.travel_window && (data.travel_window.depart || data.travel_window.home_buffer || data.travel_window.legacy) && (
            <div className="space-y-1 mt-3" data-testid="suite-foyer-travel-window">
              <p className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-white/45">
                Travel Window
              </p>
              {data.travel_window.depart && (
                <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/65"
                   data-testid="suite-foyer-travel-window-depart">
                  Depart: <span className="text-white/85 normal-case tracking-normal font-serif italic">{data.travel_window.depart}</span>
                </p>
              )}
              {data.travel_window.home_buffer && (
                <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/65"
                   data-testid="suite-foyer-travel-window-home">
                  Home Buffer: <span className="text-white/85 normal-case tracking-normal font-serif italic">{data.travel_window.home_buffer}</span>
                </p>
              )}
              {!data.travel_window.depart && !data.travel_window.home_buffer && data.travel_window.legacy && (
                <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/60">
                  {data.travel_window.legacy}
                </p>
              )}
            </div>
          )}
        </section>

        {(data.welcome_copy || data.welcome_copy_second) && (
          <section className="mt-10 space-y-5 max-w-xl">
            {data.welcome_copy && (
              <p
                className="font-accent italic text-[15px] sm:text-[16px] leading-[1.85] text-white/75"
                data-testid="suite-foyer-welcome-1"
              >
                {data.welcome_copy}
              </p>
            )}
            {data.welcome_copy_second && (
              <p
                className="font-accent italic text-[15px] sm:text-[16px] leading-[1.85] text-white/65"
                data-testid="suite-foyer-welcome-2"
              >
                {data.welcome_copy_second}
              </p>
            )}
          </section>
        )}

        {/* Feb 2026 — Permanent Suite Note. Locked default; advisor may
            override it on the trip, but AI never rewrites it. */}
        {data.suite_note && (
          <section className="mt-8 max-w-xl" data-testid="suite-foyer-suite-note">
            <p className="pl-4 border-l border-[#c9a961]/40 font-accent italic text-[14.5px] sm:text-[15px] leading-[1.8] text-white/80">
              {data.suite_note}
            </p>
          </section>
        )}

        {/* CTA */}
        <div className="mt-12 sm:mt-16 space-y-4">
          <button
            type="button"
            onClick={enterSuite}
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#c9a961] hover:bg-[#d4b574] text-black font-mono text-[11px] sm:text-[12px] uppercase tracking-[0.28em] transition-colors"
            data-testid="suite-foyer-cta"
          >
            {(data.cta_label || "Enter Your Suite").toString()}
            <ArrowRight className="size-4" strokeWidth={1.5} />
          </button>

          {/* Feb 2026 — Helper text under the button intentionally removed
              per spec; the button text alone is enough on the foyer. */}
        </div>

        {/* Quiet footer */}
        <footer className="mt-24 sm:mt-32 pt-8 border-t border-white/10 space-y-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">
            {data.footer || "VÉRAULT TRAVELS · PRIVATE SUITE"}
          </p>
          {data.footer_subtext && (
            <p className="font-accent italic text-[12px] text-white/40">
              {data.footer_subtext}
            </p>
          )}
        </footer>
      </div>
    </main>
  );
}

// Feb 2026 — Soft Carousel hero. Slow fade between images, pauses on
// hover/touch, respects reduced-motion. Opt-in only — never default.
function SoftCarouselHero({ images }) {
  const list = (images || []).filter(Boolean);
  const reduced = React.useMemo(
    () => typeof window !== "undefined" && window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );
  const [idx, setIdx] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  React.useEffect(() => {
    if (list.length < 2 || reduced || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 6500);
    return () => clearInterval(t);
  }, [list.length, reduced, paused]);
  if (!list.length) return null;
  return (
    <div
      className="mt-10 sm:mt-14 relative overflow-hidden rounded-sm aspect-[3/4] sm:aspect-[16/10]"
      data-testid="suite-foyer-hero"
      data-layout="soft_carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {list.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1800ms] ease-in-out"
          style={{ opacity: i === idx ? 0.92 : 0 }}
          aria-hidden={i !== idx}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
    </div>
  );
}

// Feb 2026 — Removed "Continue where you left off" memory card per
// product direction. The Foyer is a clean welcome surface — clients
// re-enter through the primary CTA, not a secondary chip.

